import { useState, useEffect, useRef } from 'react';
import { EngraveSettings, TextAlignment } from '../types';
import { ImageValidationService } from '../services/imageValidationService';
import { getPresetById, getPresetsForFace } from '../presets/presets';

interface TextInputProps {
  settings: EngraveSettings;
  onSettingsChange: (settings: EngraveSettings) => void;
  isFrontFace: boolean;
  onLoadFromClipboard?: () => void;
  presetId?: string;
  onPresetChange?: (presetId: string) => void;
}

// 使用可能なフォント一覧（商用利用可能なフォントのみ）
const AVAILABLE_FONTS = [
  'Noto Sans JP',
  'Noto Serif JP',
  'Sawarabi Mincho',
  'MOBO',
  'URW Gothic'
];

const MAX_CHARACTERS = 200;

/**
 * テキスト入力コンポーネント
 * 彫刻設定（テキスト、フォント、サイズ、位置など）を入力
 */
export const TextInput: React.FC<TextInputProps> = ({ settings, onSettingsChange, isFrontFace, onLoadFromClipboard, presetId = '', onPresetChange }) => {
  const [localSettings, setLocalSettings] = useState<EngraveSettings>(settings);
  const [imageValidationErrors, setImageValidationErrors] = useState<string[]>([]);
  const [imageValidationWarnings, setImageValidationWarnings] = useState<string[]>([]);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string>(presetId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // propsのsettingsが変更されたらlocalSettingsを更新
  useEffect(() => {
    setLocalSettings(settings);
    // LocalStorageから復元された画像の警告を表示
    if (settings.imageValidationWarnings && settings.imageValidationWarnings.length > 0) {
      setImageValidationWarnings(settings.imageValidationWarnings);
    } else {
      setImageValidationWarnings([]);
    }
    // タブ切り替え時にファイル入力欄をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [settings]);

  // propsのpresetIdが変更されたらselectedPresetIdを更新
  useEffect(() => {
    setSelectedPresetId(presetId);
  }, [presetId]);

  const handleChange = (field: keyof EngraveSettings, value: any) => {
    const newSettings = { ...localSettings, [field]: value };
    setLocalSettings(newSettings);
    onSettingsChange(newSettings);

    // 手動変更時はプリセット選択をクリア
    if (selectedPresetId && onPresetChange) {
      setSelectedPresetId('');
      onPresetChange('');
    }
  };

  /**
   * プリセット選択ハンドラ（トグル動作）
   */
  const handlePresetSelect = (presetId: string) => {
    if (localSettings.isImageMode) {
      return; // 画像モード時は何もしない
    }

    // 既に選択されている場合は選択解除
    if (selectedPresetId === presetId) {
      setSelectedPresetId('');
      if (onPresetChange) {
        onPresetChange('');
      }
      return;
    }

    // 新しいプリセットを選択
    setSelectedPresetId(presetId);

    if (onPresetChange) {
      onPresetChange(presetId);
    }

    const preset = getPresetById(presetId);
    if (preset) {
      // プリセットの設定を適用
      const newSettings = {
        ...localSettings,
        ...preset.settings
      };
      setLocalSettings(newSettings);
      onSettingsChange(newSettings);
    }
  };

  /**
   * 画像ファイルが選択されたときの処理
   */
  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setIsProcessingImage(true);
    setImageValidationErrors([]);
    setImageValidationWarnings([]);

    try {
      // 画像をバリデート
      const validationResult = await ImageValidationService.validateImageFile(file);

      if (!validationResult.isValid) {
        setImageValidationErrors(validationResult.errors);
        setImageValidationWarnings(validationResult.warnings);
        // ファイル入力をリセット
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // バリデーション成功：警告がある場合は表示
      setImageValidationWarnings(validationResult.warnings);

      // 画像をbase64に変換
      const imageDataUrl = await ImageValidationService.fileToBase64(file);

      // 設定を更新（画像モードに切り替え）
      const newSettings: EngraveSettings = {
        ...localSettings,
        isImageMode: true,
        imageData: imageDataUrl,
        imageFileName: file.name,
        imageValidationWarnings: validationResult.warnings
      };

      setLocalSettings(newSettings);
      onSettingsChange(newSettings);
    } catch (error) {
      setImageValidationErrors([`画像の処理に失敗しました: ${error instanceof Error ? error.message : String(error)}`]);
      // ファイル入力をリセット
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setIsProcessingImage(false);
    }
  };

  /**
   * 画像を削除してテキストモードに戻す
   */
  const handleRemoveImage = () => {
    const newSettings: EngraveSettings = {
      ...localSettings,
      isImageMode: false,
      imageData: undefined,
      imageFileName: undefined,
      imageValidationWarnings: undefined
    };

    setLocalSettings(newSettings);
    onSettingsChange(newSettings);
    setImageValidationErrors([]);
    setImageValidationWarnings([]);

    // ファイル入力をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const availablePresets = getPresetsForFace(isFrontFace ? 'front' : 'back');

  return (
    <div className="text-input-container">
      {/* プリセット選択セクション */}
      {availablePresets.length > 0 && (
        <div className="form-group mb-3">
          <label className="form-label">
            プリセット
            {localSettings.isImageMode && <span className="text-muted ms-2">（画像モード時は無効）</span>}
          </label>
          <div className="d-flex flex-wrap gap-2">
            {availablePresets.map(preset => (
              <div
                key={preset.id}
                className={`preset-card ${selectedPresetId === preset.id ? 'selected' : ''} ${localSettings.isImageMode ? 'disabled' : ''}`}
                onClick={() => handlePresetSelect(preset.id)}
                role="button"
                tabIndex={localSettings.isImageMode ? -1 : 0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePresetSelect(preset.id);
                  }
                }}
                aria-pressed={selectedPresetId === preset.id}
                aria-disabled={localSettings.isImageMode}
              >
                <div className="preset-card-name">{preset.name}</div>
                {preset.description && (
                  <div className="preset-card-description">{preset.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 画像アップロードセクション */}
      <div className="form-group mb-3">
        <label className="form-label">グレースケール画像</label>
        {localSettings.isImageMode && localSettings.imageFileName ? (
          // 画像モード時：ファイル名と削除ボタンを表示
          <div className="d-flex align-items-center gap-2">
            <input
              type="text"
              className="form-control"
              value={localSettings.imageFileName}
              readOnly
              disabled
            />
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleRemoveImage}
              title="画像の選択を解除"
              aria-label="画像の選択を解除"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        ) : (
          // テキストモード時：ファイル選択入力を表示
          <input
            ref={fileInputRef}
            type="file"
            className="form-control"
            accept="image/png"
            onChange={handleImageSelect}
            disabled={isProcessingImage}
          />
        )}

        <small className="form-text text-muted">
          PNG形式、20.1×40.6mmのグレースケール画像<br/>
          （推奨: 396×800px @500dpi）
        </small>

        {/* 画像処理中の表示 */}
        {isProcessingImage && (
          <div className="alert alert-info mt-2">
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">処理中...</span>
            </div>
            画像を処理中...
          </div>
        )}

        {/* 画像バリデーションエラーの表示 */}
        {imageValidationErrors.length > 0 && (
          <div className="alert alert-danger mt-2">
            <strong>画像エラー</strong>
            <ul className="mb-0 list-unstyled">
              {imageValidationErrors.map((error, index) => (
                <li key={index} style={{ whiteSpace: 'pre-line' }}>
                  <i className="bi bi-x-circle-fill text-danger me-2"></i>
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 画像バリデーション警告の表示 */}
        {imageValidationWarnings.length > 0 && (
          <div className="alert alert-warning mt-2">
            <strong>警告</strong>
            <ul className="mb-0 list-unstyled">
              {imageValidationWarnings.map((warning, index) => (
                <li key={index}>
                  <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                  {warning}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 画像モード時のプレビュー */}
        {localSettings.isImageMode && localSettings.imageData && (
          <div className="mt-2">
            <div className="alert alert-success">
              画像が読み込まれました。プレビューで確認してください。
            </div>
          </div>
        )}
      </div>

      {/* テキスト入力セクション（画像モード時は無効化） */}
      <div className="form-group mb-3">
        <label htmlFor="engraveText" className="form-label">
          入れる文字列
          {localSettings.isImageMode && <span className="text-muted ms-2">（画像モード時は無効）</span>}
        </label>
        <textarea
          id="engraveText"
          className="form-control"
          value={localSettings.text}
          onChange={(e) => handleChange('text', e.target.value)}
          placeholder="入れたい文字を入力してください"
          rows={4}
          maxLength={MAX_CHARACTERS}
          disabled={localSettings.isImageMode}
        />
        <small className="form-text text-muted">
          {localSettings.text.length} / {MAX_CHARACTERS} 文字
          {localSettings.text.length > MAX_CHARACTERS && (
            <span className="text-danger">（文字数制限を超えています）</span>
          )}
        </small>
      </div>

      <div className="form-group mb-3">
        <label htmlFor="fontFamily" className="form-label">
          フォント
          {localSettings.isImageMode && <span className="text-muted ms-2">（画像モード時は無効）</span>}
        </label>
        <select
          id="fontFamily"
          className="form-control"
          value={localSettings.fontFamily}
          onChange={(e) => handleChange('fontFamily', e.target.value)}
          disabled={localSettings.isImageMode}
        >
          {AVAILABLE_FONTS.map(font => (
            <option key={font} value={font}>{font}</option>
          ))}
        </select>
      </div>

      <div className="form-group mb-3">
        <label htmlFor="fontSize" className="form-label">
          フォントサイズ (pt)
          {localSettings.isImageMode && <span className="text-muted ms-2">（画像モード時は無効）</span>}
        </label>
        <input
          type="number"
          id="fontSize"
          className="form-control"
          value={localSettings.fontSize}
          onChange={(e) => {
            const value = e.target.value;
            // 空文字列の場合は入力欄の表示のみ更新（プレビューは更新しない）
            if (value === '') {
              setLocalSettings({ ...localSettings, fontSize: '' as any });
              return;
            }
            // 数値に変換して有効な値の場合のみプレビューを更新
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
              handleChange('fontSize', numValue);
            }
          }}
          onBlur={(e) => {
            const value = e.target.value;
            // フォーカスが外れたときに空または無効な値の場合は最小値に設定
            if (value === '' || isNaN(parseFloat(value))) {
              handleChange('fontSize', 6);
            } else {
              const numValue = parseFloat(value);
              // 範囲外の値を修正
              if (numValue < 6) {
                handleChange('fontSize', 6);
              } else if (numValue > 100) {
                handleChange('fontSize', 100);
              }
            }
          }}
          min="6"
          max="100"
          step="0.5"
          disabled={localSettings.isImageMode}
        />
        <small className="form-text text-muted">6〜100 pt</small>
      </div>

      {/* テキスト関連のコントロール（画像モード時は非表示） */}
      {!localSettings.isImageMode && (
        <>
          <div className="form-group mb-3">
            <div className="text-style-controls">
              <div className="alignment-control-item">
                <span className="alignment-label">方向</span>
                <div className="icon-button-group">
                  <button
                    type="button"
                    className={`icon-button ${!localSettings.isVertical ? 'active' : ''}`}
                    onClick={() => handleChange('isVertical', false)}
                    title="横"
                  >
                    <i className="bi bi-arrows"></i>
                  </button>
                  <button
                    type="button"
                    className={`icon-button ${localSettings.isVertical ? 'active' : ''}`}
                    onClick={() => handleChange('isVertical', true)}
                    title="縦"
                  >
                    <i className="bi bi-arrows-vertical"></i>
                  </button>
                </div>
              </div>
              <div className="alignment-control-item">
                <span className="alignment-label">スタイル</span>
                <div className="icon-button-group">
                  <button
                    type="button"
                    className={`icon-button ${localSettings.isBold ? 'active' : ''}`}
                    onClick={() => handleChange('isBold', !localSettings.isBold)}
                    title="太字"
                  >
                    <i className="bi bi-type-bold"></i>
                  </button>
                  <button
                    type="button"
                    className={`icon-button ${localSettings.isItalic ? 'active' : ''}`}
                    onClick={() => handleChange('isItalic', !localSettings.isItalic)}
                    title="斜体"
                  >
                    <i className="bi bi-type-italic"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="form-group mb-3">
            <div className="alignment-control-item">
              <span className="alignment-label">行揃え</span>
              <div className="icon-button-group">
                <button
                  type="button"
                  className={`icon-button ${localSettings.alignment === TextAlignment.Left ? 'active' : ''}`}
                  onClick={() => handleChange('alignment', TextAlignment.Left)}
                  title="左揃え"
                >
                  <i className="bi bi-text-left"></i>
                </button>
                <button
                  type="button"
                  className={`icon-button ${localSettings.alignment === TextAlignment.Center ? 'active' : ''}`}
                  onClick={() => handleChange('alignment', TextAlignment.Center)}
                  title="中央揃え"
                >
                  <i className="bi bi-text-center"></i>
                </button>
                <button
                  type="button"
                  className={`icon-button ${localSettings.alignment === TextAlignment.Right ? 'active' : ''}`}
                  onClick={() => handleChange('alignment', TextAlignment.Right)}
                  title="右揃え"
                >
                  <i className="bi bi-text-right"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="form-group mb-3">
            <div className="alignment-controls">
              <div className="alignment-control-item">
                <span className="alignment-label">水平</span>
                <div className="icon-button-group">
                  <button
                    type="button"
                    className={`icon-button ${localSettings.horizontalAlignment === 0 ? 'active' : ''}`}
                    onClick={() => handleChange('horizontalAlignment', 0)}
                    title="左揃え"
                  >
                    <i className="bi bi-align-start"></i>
                  </button>
                  <button
                    type="button"
                    className={`icon-button ${localSettings.horizontalAlignment === 1 ? 'active' : ''}`}
                    onClick={() => handleChange('horizontalAlignment', 1)}
                    title="中央揃え"
                  >
                    <i className="bi bi-align-center"></i>
                  </button>
                  <button
                    type="button"
                    className={`icon-button ${localSettings.horizontalAlignment === 2 ? 'active' : ''}`}
                    onClick={() => handleChange('horizontalAlignment', 2)}
                    title="右揃え"
                  >
                    <i className="bi bi-align-end"></i>
                  </button>
                </div>
              </div>
              <div className="alignment-control-item">
                <span className="alignment-label">垂直</span>
                <div className="icon-button-group">
                  <button
                    type="button"
                    className={`icon-button ${localSettings.verticalAlignment === 0 ? 'active' : ''}`}
                    onClick={() => handleChange('verticalAlignment', 0)}
                    title="上揃え"
                  >
                    <i className="bi bi-align-top"></i>
                  </button>
                  <button
                    type="button"
                    className={`icon-button ${localSettings.verticalAlignment === 1 ? 'active' : ''}`}
                    onClick={() => handleChange('verticalAlignment', 1)}
                    title="中央揃え"
                  >
                    <i className="bi bi-distribute-vertical"></i>
                  </button>
                  <button
                    type="button"
                    className={`icon-button ${localSettings.verticalAlignment === 2 ? 'active' : ''}`}
                    onClick={() => handleChange('verticalAlignment', 2)}
                    title="下揃え"
                  >
                    <i className="bi bi-align-bottom"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* クリップボードから設定を読み込み */}
      {onLoadFromClipboard && (
        <div className="mt-4 pt-3 border-top">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm w-100"
            onClick={onLoadFromClipboard}
            title="Ctrl+Lでもアクセス可能"
          >
            <i className="bi bi-clipboard"></i> クリップボードから設定を読み込む
          </button>
        </div>
      )}
    </div>
  );
};
